import React, { useState } from 'react';
import './ServicesPage.css';
import coordinatedCareImg from '../../images/coordinated-care-framework.png';

const ServicesPage = () => {
  const [openIndices, setOpenIndices] = useState(new Set());

  const toggleIndex = (index) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const accordionItems = [
    {
      title: 'Chronic Care Management (CCM)',
      content: (
        <>
          <p>Our Chronic Care Management program supports patients living with two or more chronic conditions through structured, ongoing engagement between visits.</p>
          <p className="accordion-subheading">We provide:</p>
          <ul className="accordion-bullets">
            <li>Comprehensive care plan development and revision</li>
            <li>Monthly nurse-led engagement and clinical assessment</li>
            <li>Medication reconciliation and adherence reinforcement</li>
            <li>Coordination across providers and care settings</li>
            <li>Early identification and escalation of clinical concerns</li>
            <li>Structured documentation aligned with compliance standards</li>
          </ul>
          <p>Our CCM model strengthens continuity, reinforces care plans, and supports sustainable program performance.</p>
        </>
      )
    },
    {
      title: 'Principal Care Management (PCM)',
      content: (
        <>
          <p>Principal Care Management provides focused support for patients living with a single serious, high-risk chronic condition.</p>
          <p>Our nurses deliver condition-specific monitoring, reinforcement, and coordination designed to improve outcomes and reduce avoidable escalation.</p>
          <p className="accordion-subheading">PCM includes:</p>
          <ul className="accordion-bullets">
            <li>Individualized care planning</li>
            <li>Targeted symptom monitoring</li>
            <li>Medication oversight and adherence support</li>
            <li>Timely communication with providers</li>
            <li>Structured documentation and reporting</li>
          </ul>
          <p>This program is ideal for specialty practices seeking deeper engagement for high-acuity populations.</p>
        </>
      )
    },
    {
      title: 'Remote Patient Monitoring (RPM)',
      content: (
        <>
          <p>Remote Patient Monitoring enables real-time insight into patient health trends between visits.</p>
          <p>We support implementation, monitoring, and escalation - ensuring collected data translates into meaningful clinical action.</p>
          <p className="accordion-subheading">RPM capabilities include:</p>
          <ul className="accordion-bullets">
            <li>Vital sign monitoring and trend analysis</li>
            <li>Device onboarding and patient education</li>
            <li>Alert management and escalation protocols</li>
            <li>Clinical documentation and provider communication</li>
          </ul>
          <p>Our RPM model is designed to integrate smoothly into existing workflows while enhancing proactive care delivery.</p>
        </>
      )
    },
    {
      title: 'Transitional Care Management (TCM)',
      content: (
        <>
          <p>Transitional Care Management supports patients following hospital discharge - when risk for readmission and complications is highest.</p>
          <p>We ensure timely outreach, medication reconciliation, follow-up coordination, and structured communication with providers during this critical transition period.</p>
          <p className="accordion-subheading">TCM includes:</p>
          <ul className="accordion-bullets">
            <li>Post-discharge patient outreach</li>
            <li>Medication review and reconciliation</li>
            <li>Follow-up appointment coordination</li>
            <li>Reinforcement of discharge instructions</li>
            <li>Escalation of emerging concerns</li>
            <li>Documentation aligned with regulatory requirements</li>
          </ul>
          <p>Our TCM program strengthens continuity during care transitions while supporting readmission reduction efforts.</p>
        </>
      )
    },
    {
      title: 'Principal Illness Navigation (PIN)',
      content: (
        <>
          <p>Principal Illness Navigation provides structured, non-clinical support for patients managing a serious or high-risk condition.</p>
          <p>This service focuses on reducing barriers to care, improving follow-through, and strengthening coordination - allowing clinical teams to focus on direct medical decision-making while patients receive consistent guidance throughout their journey.</p>
          <p className="accordion-subheading">PIN includes:</p>
          <ul className="accordion-bullets">
            <li>Appointment coordination and follow-up tracking</li>
            <li>Referral management and specialty navigation</li>
            <li>Transportation and access support</li>
            <li>Insurance and authorization guidance</li>
            <li>Social determinants of health screening and resource connection</li>
            <li>Reinforcement of care plan logistics and next steps</li>
          </ul>
          <p>Our navigation model is designed to support patients facing complex treatment pathways - ensuring they do not fall through gaps between visits, providers, or services.</p>
          <p>By addressing logistical and access challenges proactively, Principal Illness Navigation strengthens adherence, improves continuity, and supports overall program performance.</p>
        </>
      )
    }
  ];

  return (
    <>
      <div className="services-page">
        <section className="our-services-hero">
          <div className="container our-services-section">
            <h1 className="our-services-title">Our Services</h1>
            <p className="our-services-intro">Solace Care 360 delivers structured, nurse-led care management programs designed to support patient outcomes, documentation integrity, and sustainable reimbursement.</p>
            <p className="our-services-body">Each service line is implemented with clinical discipline, operational alignment, and workflow integration—ensuring care management feels like an extension of your practice, not an add-on.</p>
          </div>
        </section>

        <section className="services-accordion-section">
          <div className="accordion-list">
            {accordionItems.map((item, index) => (
              <div key={index} className="accordion-item">
                <button
                  className="accordion-header"
                  onClick={() => toggleIndex(index)}
                >
                  {item.title}
                  <span className="accordion-icon">{openIndices.has(index) ? '−' : '+'}</span>
                </button>
                {openIndices.has(index) && (
                  <div className="accordion-content">
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="coordinated-care-section">
          <div className="container">
            <div className="coordinated-care-row">
              <div className="coordinated-care-graphic">
                <img src={coordinatedCareImg} alt="" className="coordinated-care-graphic-img" />
              </div>
              <div className="coordinated-care-content">
                <h2 className="coordinated-care-title">A Coordinated Care Management Framework</h2>
                <p className="coordinated-care-intro">Each Solace Care 360 service line can operate independently.</p>
                <p className="coordinated-care-body">However, our programs are designed to function as a coordinated care management ecosystem - supporting patients across chronic management, acute transitions, remote monitoring, and principal illness navigation.</p>
                <p className="coordinated-care-close">We work with practices to determine the right combination of services based on patient acuity, workflow structure, and performance goals - ensuring care management is aligned, sustainable, and clinically sound.</p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ServicesPage;
